import { useContext, useState } from "react";
import {
  Avatar,
  Menu,
  MenuItem,
  AppBar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
} from "@mui/material";
import {
  AppsRounded as AppsRoundedIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import theme from "../../theme";
import { getFilteredNavigationItems } from "../../utils/accountPermission";
import { appBarStyle } from "./NavBar.styles";
import { LogoutButton } from "../Login/UserLogout";
import { useAccountInfo } from "../../hooks/useAccountInfo";
import { AuthContext } from "../Auth/AuthProvider";
import { AccountSwitcher } from "./AccountSwitcher";

const ANVIL_PORTAL_URL = "https://app.anvilanalytical.com/";
const DRAWER_WIDTH = 280;
const LOGO_PATH = "/assets/black-anvil-logo.png";

export function NavBar() {
  // Account Info from hooks
  const { accountInfo, accounts } = useAccountInfo();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);

  const authContext = useContext(AuthContext);

  const location = useLocation();
  const isMenuOpen = Boolean(anchorEl);
  const navigationItems = getFilteredNavigationItems(accountInfo);

  // Determine page title based on current route
  const getDynamicTitle = () => {
    const path = location.pathname;

    const findTitleInItems = (items, currentPath) => {
      for (const item of items) {
        if (item.path === currentPath) {
          return item.label;
        }
        if (item.children) {
          const childTitle = findTitleInItems(item.children, currentPath);
          if (childTitle) {
            return `${item.label} - ${childTitle}`;
          }
        }
      }
      return null;
    };

    if (path === "/Home") {
      return "Generate Insight";
    }

    return findTitleInItems(navigationItems, path) || "Generate Insight";
  };

  // Drawer handlers
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleDrawerClose = () => setDrawerOpen(false);

  // Menu handlers
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAnvilPortalLink = () => {
    window.open(ANVIL_PORTAL_URL, "_blank", "noopener,noreferrer");
  };

  // Navigation expansion handler
  const handleToggleExpand = (itemLabel) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemLabel]: !prev[itemLabel],
    }));
  };

  // Route checking utilities
  const isCurrentPage = (path) => location.pathname === path;

  const isParentCurrentPage = (item) => {
    if (!item.children) return false;
    return item.children.some((child) => location.pathname === child.path);
  };

  // Render individual navigation items recursively
  const renderNavigationItem = (item, isChild = false) => {
    const isCurrent = isCurrentPage(item.path);
    const isParentCurrent = isParentCurrentPage(item);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <Box key={item.label}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            component={hasChildren ? "div" : Link}
            to={hasChildren ? undefined : item.path}
            onClick={() => {
              if (hasChildren) {
                handleToggleExpand(item.label);
              } else {
                handleDrawerClose();
              }
            }}
            disabled={isCurrent && !hasChildren}
            sx={{
              minHeight: 48,
              px: isChild ? 4 : 2.5,
              py: 1.5,
              mx: 1,
              my: 0.5,
              borderRadius: 1,
              backgroundColor:
                isCurrent || isParentCurrent
                  ? theme.palette.primary.background
                  : "transparent",
              border:
                isCurrent || isParentCurrent
                  ? "1px solid #d3e3fe"
                  : "1px solid transparent",
              transition: "all 0.2s ease-in-out",
              "&:hover": !(isCurrent && !hasChildren)
                ? {
                    backgroundColor: theme.palette.primary.lightBackground,
                    boxShadow: theme.customShadows.light,
                    transform: "translateY(-1px)",
                  }
                : {},
              "&.Mui-disabled": {
                opacity: 1,
                color: "inherit",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  variant: "body1",
                  fontWeight: isCurrent || isParentCurrent ? 500 : 400,
                  color:
                    isCurrent || isParentCurrent
                      ? theme.palette.primary.main
                      : "inherit",
                },
              }}
            />
            {hasChildren && (
              <Box sx={{ ml: 1 }}>
                {expandedItems[item.label] ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={expandedItems[item.label]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children
                .filter((child) => !child.hideInNav)
                .map((child) => renderNavigationItem(child, true))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  // Page state checks
  const isHomepage = location.pathname === "/Home";
  const isStartingPage = location.pathname === "/";
  const showMenuButton = !isHomepage && !isStartingPage;

  // Drawer content
  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH, height: "100%" }}>
      {/* Drawer Header */}
      <Box
        sx={{
          p: "11px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          component="img"
          src={LOGO_PATH}
          alt="Anvil logo"
          sx={{ height: 30, width: 30, mr: 2 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            Anvil Insight
          </Typography>
          {accounts.length > 1 && accountInfo?.accountName && (
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: "block",
                color: theme.palette.primary.brandSecondary,
                fontWeight: 500,
              }}
            >
              {accountInfo.accountName}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Navigation Items */}
      <Box sx={{ py: 1 }}>
        <List>{navigationItems.map((item) => renderNavigationItem(item))}</List>
      </Box>

      {/* Portal Link at Bottom */}
      <Box
        sx={{
          mt: "auto",
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          variant="text"
          onClick={handleAnvilPortalLink}
          fullWidth
          sx={{
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.white,
              borderColor: theme.palette.primary.main,
            },
          }}
        >
          Anvil Portal
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* App Bar */}
      <AppBar sx={appBarStyle} elevation={0}>
        {/* Menu Toggle Button */}
        {showMenuButton && (
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            aria-label="Open navigation menu"
            sx={{
              color: "primary.main",
              backgroundColor: "transparent",
              boxShadow: "none",
              mx: 2,
              "&:hover": {
                backgroundColor: "transparent",
                color: "primary.main",
              },
              "& .MuiSvgIcon-root": {
                width: 24,
                height: 24,
              },
            }}
          >
            <AppsRoundedIcon />
          </IconButton>
        )}

        {/* Logo and Title */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={LOGO_PATH}
            alt="Anvil logo"
            sx={{ height: 30, width: 30, mr: 2 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {getDynamicTitle()}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <AccountSwitcher accounts={accounts} accountInfo={accountInfo} />

        {/* User Avatar Menu */}
        <IconButton
          onClick={handleMenuOpen}
          disableRipple
          sx={{
            mr: 2,
            "&:active": {
              transform: "none",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
          aria-label="Open user menu"
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 32,
              height: 32,
              "&:hover": {
                border: `0px solid ${theme.palette.primary.main}`,
              },
            }}
          >
            {authContext?.userInfo?.first_name?.charAt(0)}
          </Avatar>
        </IconButton>

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          disableScrollLock
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1.5,
                "& .MuiMenuItem-root .MuiAvatar-root": {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                "&::before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              handleAnvilPortalLink();
            }}
          >
            Anvil Portal
          </MenuItem>
          <Divider />
          <LogoutButton />
        </Menu>
      </AppBar>

      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        disableScrollLock
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: DRAWER_WIDTH,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
